import { useCallback, useEffect, useRef, useState } from 'react'
import { ChevronDown, ChevronRight, Crosshair, Maximize2, ZoomIn, ZoomOut } from 'lucide-react'

import styles from './GtContextStrip.module.scss'

import type { GtTurn } from '@/api/internal'
import Button from '@/components/Button'
import type { useGtArtifacts } from '@/pages/app/AttributionViewer/annotate/useGtArtifacts'
import type { useGtAudio } from '@/pages/app/AttributionViewer/annotate/useGtAudio'
import Legend from '@/pages/app/AttributionViewer/components/Legend'
import Swimlane, {
  formatMs,
  MIN_TIMELINE_VIEW_MS,
  type ViewportChange,
  zoomViewport,
} from '@/pages/app/AttributionViewer/components/Swimlane'

export interface GtContextStripProps {
  activeTurn: GtTurn | null
  audio: ReturnType<typeof useGtAudio>
  artifacts: ReturnType<typeof useGtArtifacts>
  onSeek: (ms: number) => void
  onPlayToggle: () => void
}

const FOCUS_PAD_MS = 15_000
const COLLAPSED_KEY = 'gt:contextStripCollapsed'

const readCollapsed = (): boolean => {
  try {
    return window.localStorage.getItem(COLLAPSED_KEY) === '1'
  } catch {
    return false
  }
}

const writeCollapsed = (collapsed: boolean) => {
  try {
    window.localStorage.setItem(COLLAPSED_KEY, collapsed ? '1' : '0')
  } catch {
    // Preference only; losing it is harmless.
  }
}

const viewportAround = (center: number, width: number, endMs: number): ViewportChange => {
  const from = Math.min(Math.max(0, center - width / 2), Math.max(0, endMs - width))
  return { from, to: Math.min(endMs, from + width) }
}

export const GtContextStrip = ({ activeTurn, audio, artifacts, onSeek, onPlayToggle }: GtContextStripProps) => {
  const {
    lanes,
    endMs,
    speakerStyles,
    speakerMappings,
    speakerMapState,
    unassignedPeople,
    hasObservations,
    observationPlatform,
    observationOs,
    onRetrySpeakerMap,
  } = artifacts

  const [collapsed, setCollapsed] = useState(readCollapsed)
  const [showTimeline, setShowTimeline] = useState(true)
  const [showLegend, setShowLegend] = useState(false)
  const [viewport, setViewportState] = useState<{
    from: number
    to: number
  } | null>(null)
  const [hoverTime, setHoverTime] = useState<number | null>(null)
  const [pinnedTime, setPinnedTime] = useState<number | null>(null)

  const viewStart = Math.min(Math.max(0, viewport?.from ?? 0), Math.max(0, endMs - 1))
  const viewEnd = Math.min(endMs, Math.max(viewStart + 1, viewport?.to ?? endMs))
  const isZoomed = viewStart > 0 || viewEnd < endMs
  const playheadTime = (audio.isPlaying ? audio.audioTime : pinnedTime) ?? audio.audioTime
  const zoomCenter = hoverTime ?? playheadTime ?? (viewStart + viewEnd) / 2
  const minimumView = Math.min(MIN_TIMELINE_VIEW_MS, endMs)

  const viewportFrameRef = useRef<number | null>(null)
  const pendingViewportRef = useRef<ViewportChange | null>(null)

  const cancelPendingViewport = useCallback(() => {
    if (viewportFrameRef.current != null) window.cancelAnimationFrame(viewportFrameRef.current)
    viewportFrameRef.current = null
    pendingViewportRef.current = null
  }, [])

  useEffect(() => cancelPendingViewport, [cancelPendingViewport])

  const writeViewport = useCallback(
    ({ from, to }: ViewportChange) => {
      if (from <= 0 && to >= endMs) setViewportState(null)
      else setViewportState({ from: Math.round(from), to: Math.round(to) })
    },
    [endMs]
  )

  const setViewport = useCallback(
    (change: ViewportChange) => {
      if (!change.replace) {
        cancelPendingViewport()
        writeViewport(change)
        return
      }
      pendingViewportRef.current = change
      if (viewportFrameRef.current != null) return
      viewportFrameRef.current = window.requestAnimationFrame(() => {
        viewportFrameRef.current = null
        const pending = pendingViewportRef.current
        pendingViewportRef.current = null
        if (pending) writeViewport(pending)
      })
    },
    [cancelPendingViewport, writeViewport]
  )

  const resetZoom = useCallback(() => {
    cancelPendingViewport()
    setViewportState(null)
  }, [cancelPendingViewport])

  const viewRef = useRef({ viewStart, viewEnd, endMs })
  viewRef.current = { viewStart, viewEnd, endMs }

  useEffect(() => {
    if (!activeTurn) return
    const view = viewRef.current
    if (view.viewStart <= 0 && view.viewEnd >= view.endMs) return
    if (activeTurn.start_ms >= view.viewStart && activeTurn.end_ms <= view.viewEnd) return
    cancelPendingViewport()
    const center = (activeTurn.start_ms + activeTurn.end_ms) / 2
    writeViewport(viewportAround(center, view.viewEnd - view.viewStart, view.endMs))
  }, [activeTurn, cancelPendingViewport, writeViewport])

  const zoomToTurn = () => {
    if (!activeTurn) return
    cancelPendingViewport()
    const width = Math.max(minimumView, activeTurn.end_ms - activeTurn.start_ms + 2 * FOCUS_PAD_MS)
    writeViewport(viewportAround((activeTurn.start_ms + activeTurn.end_ms) / 2, width, endMs))
  }

  const zoomAround = (center: number, scale: number) => {
    const ratio = Math.min(1, Math.max(0, (center - viewStart) / Math.max(1, viewEnd - viewStart)))
    setViewport(zoomViewport(viewStart, viewEnd, ratio, scale, 0, endMs))
  }

  const onPinnedTimeChange = (time: number | null) => {
    setPinnedTime(time)
    if (time != null) onSeek(time)
  }

  const toggleCollapsed = () => {
    setCollapsed((value) => {
      writeCollapsed(!value)
      return !value
    })
  }

  const errorLanes = lanes.filter((lane) => lane.state === 'error')

  return (
    <div className={styles.strip}>
      <div className={styles.summaryRow}>
        <button type="button" className={styles.collapseToggle} aria-expanded={!collapsed} onClick={toggleCollapsed}>
          {collapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
          Streams
        </button>
        {!collapsed && (
          <>
            <button
              type="button"
              className={styles.toggle}
              aria-expanded={showTimeline}
              onClick={() => setShowTimeline((value) => !value)}
            >
              {showTimeline ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              Timeline
            </button>
            <button
              type="button"
              className={styles.toggle}
              aria-expanded={showLegend}
              onClick={() => setShowLegend((value) => !value)}
            >
              {showLegend ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              Speaker legend
            </button>
          </>
        )}
        {audio.state === 'error' && (
          <span className={styles.laneStatusError}>
            Audio unavailable
            <Button title="Retry" type="light" ariaLabel="Retry audio" width={64} onClick={audio.retry} />
          </span>
        )}
        {collapsed && (
          <span className={styles.collapsedSummary}>
            {lanes.length} streams · {formatMs(endMs)}
          </span>
        )}
        {!collapsed && showTimeline && (
          <div className={styles.timelineControls} role="group" aria-label="Timeline zoom controls">
            <span>
              {formatMs(viewStart)} – {formatMs(viewEnd)} {isZoomed ? '(zoomed)' : '(full)'}
            </span>
            <Button
              title={<Crosshair size={14} aria-hidden="true" />}
              type="light"
              className={styles.zoomButton}
              ariaLabel="Zoom to turn"
              isDisabled={!activeTurn || endMs <= 0}
              width={26}
              onClick={zoomToTurn}
            />
            <Button
              title={<ZoomOut size={14} aria-hidden="true" />}
              type="light"
              className={styles.zoomButton}
              ariaLabel="Zoom out"
              isDisabled={!isZoomed}
              width={26}
              onClick={() => zoomAround(zoomCenter, 2)}
            />
            <Button
              title={<Maximize2 size={14} aria-hidden="true" />}
              type="light"
              className={styles.zoomButton}
              ariaLabel="Reset zoom"
              isDisabled={!isZoomed}
              width={26}
              onClick={resetZoom}
            />
            <Button
              title={<ZoomIn size={14} aria-hidden="true" />}
              type="light"
              className={styles.zoomButton}
              ariaLabel="Zoom in"
              isDisabled={viewEnd - viewStart <= minimumView}
              width={26}
              onClick={() => zoomAround(zoomCenter, 0.5)}
            />
          </div>
        )}
      </div>
      {!collapsed && errorLanes.length > 0 && (
        <div className={styles.laneStatusRow}>
          {errorLanes.map((lane) => (
            <span key={lane.id} className={styles.laneStatusError}>
              {lane.label} could not be read
              {lane.onRetry && (
                <Button
                  title="Retry"
                  type="light"
                  ariaLabel={`Retry ${lane.label}`}
                  width={64}
                  onClick={lane.onRetry}
                />
              )}
            </span>
          ))}
        </div>
      )}
      {!collapsed && showTimeline && (
        <div className={styles.timelineScroll}>
          <Swimlane
            audioLane={{
              state: audio.state,
              durationMs: audio.durationMs,
              isDownloading: false,
              isPlaying: audio.isPlaying,
              onDownload: () => {
                if (audio.audioUrl) window.open(audio.audioUrl, '_blank', 'noopener')
              },
              onLoad: () => {},
              onRetry: audio.retry,
              onTogglePlayback: onPlayToggle,
            }}
            lanes={lanes}
            timelineStart={0}
            timelineEnd={endMs}
            viewStart={viewStart}
            viewEnd={viewEnd}
            speakerStyles={speakerStyles}
            hoverTime={hoverTime}
            playheadTime={playheadTime}
            pinnedTime={pinnedTime}
            onHoverTimeChange={setHoverTime}
            onPinnedTimeChange={onPinnedTimeChange}
            onViewChange={setViewport}
            onResetZoom={resetZoom}
          />
        </div>
      )}
      {!collapsed && showLegend && (
        <Legend
          hasLiveOverlap={false}
          hasObservations={hasObservations}
          observationPlatform={observationPlatform}
          observationOs={observationOs}
          onRetrySpeakerMap={onRetrySpeakerMap}
          speakerMappings={speakerMappings}
          speakerMapState={speakerMapState}
          speakerStyles={speakerStyles}
          unassignedPeople={unassignedPeople}
        />
      )}
    </div>
  )
}
